<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

interface ImageNode {
  type: 'image'
  src: string
  alt: string
  title: string | null
  raw: string
}

const props = defineProps<{
  node: ImageNode & { loading?: boolean }
}>()

const src = computed(() => props.node.src)
const alt = computed(() => props.node.alt || '')

const showPreview = ref(false)
const isVisible = ref(true)
const isThumbnailLoaded = ref(false)
const isPreviewLoaded = ref(false)

const openPreview = () => {
  showPreview.value = true
  isPreviewLoaded.value = false
}

const closePreview = () => {
  showPreview.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && showPreview.value) {
    closePreview()
  }
}

const handleThumbnailError = () => {
  isVisible.value = false
  isThumbnailLoaded.value = false
}

const handlePreviewError = () => {
  isPreviewLoaded.value = false
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <span
    v-if="isVisible"
    class="image-wrapper"
    :class="{ ready: isThumbnailLoaded }"
  >
    <img
      v-if="src"
      :src="src"
      :alt="alt"
      :title="node.title || undefined"
      class="image-thumbnail"
      :class="{ loading: !isThumbnailLoaded }"
      @load="isThumbnailLoaded = true"
      @error="handleThumbnailError"
      @click="openPreview"
    />

    <Teleport to="body">
      <Transition name="preview-fade">
        <div
          v-if="showPreview"
          class="image-preview-overlay"
          @click="closePreview"
        >
          <div
            class="preview-container"
            :class="{ ready: isPreviewLoaded }"
            @click.stop
          >
            <img
              :src="src"
              :alt="alt"
              class="preview-image"
              @load="isPreviewLoaded = true"
              @error="handlePreviewError"
              draggable="false"
            />
          </div>

          <button class="preview-close" @click="closePreview" title="关闭 (ESC)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M18 6L6 18M6 6l12 12" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </Transition>
    </Teleport>
  </span>
</template>

<style scoped lang="scss">
.image-wrapper {
  display: inline-block;
  margin: .5rem;
  vertical-align: middle;
  position: relative;
  max-height: 17rem;
  overflow: hidden;
  border-radius: $borderRadius-sm;

  &::before {
    right: 0.6rem;
    bottom: 0.1rem;
    opacity: 0;
    transition: opacity 0.2s;
    @include imgCopyrightText;
  }

  &.ready::before {
    opacity: 1;
  }
}

.image-thumbnail {
  max-width: 300px;
  max-height: 200px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: block;
  object-fit: none;
  object-position: center;
}

.image-thumbnail:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.image-thumbnail.loading {
  opacity: 0.6;
  background: #f3f4f6;
}

.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
  backdrop-filter: blur(4px);
}

.preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;

  &::before {
    right: 0.6rem;
    bottom: 0.1rem;
    opacity: 0;
    transition: opacity 0.2s;
    @include imgCopyrightText;
  }

  &.ready::before {
    opacity: 1;
  }
}

.preview-image {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: block;
  transition: transform 0.2s cubic-bezier(0.4, 2, 0.6, 1);
}

.preview-close {
  position: fixed;
  top: 18px;
  right: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #333;
  z-index: 10000;
}

.preview-close:hover {
  background: white;
  transform: scale(1.1);
}

.preview-fade-enter-active,
.preview-fade-leave-active {
  transition: opacity 0.2s;
}

.preview-fade-enter-from,
.preview-fade-leave-to {
  opacity: 0;
}

.preview-fade-enter-from .preview-container,
.preview-fade-leave-to .preview-container {
  transform: scale(0.9);
}
</style>